
import { useMemo } from "react";
import i18n, { getActivePseudoTag } from "i18n";
import { calculateEndDate, parseDate } from "../utils/dateUtils";

// Month names on the scale come from the browser's CLDR data via Intl, not
// from the translation bundle — dates are locale formatting, not translatable
// keys. Pseudo modes show plain English months: bracketing would suggest a
// missing key where none can exist.
const intlLocale = () => (getActivePseudoTag() ? "en" : i18n.language || "en");

const formatterCache = new Map();

// Returns a wx-react-gantt scale format function (cell start date -> label)
// rendering in the active UI locale. Formatters are cached per locale+options;
// the locale is read at format time so the scale follows the app language.
export const localisedDate = (options) => (date) => {
	const locale = intlLocale();
	const cacheKey = `${locale}|${JSON.stringify(options)}`;

	let formatter = formatterCache.get(cacheKey);
	if (!formatter) {
		try {
			formatter = new Intl.DateTimeFormat(locale, options);
		} catch {
			formatter = new Intl.DateTimeFormat("en", options);
		}
		formatterCache.set(cacheKey, formatter);
	}

	return formatter.format(date);
};


// Calculate optimal zoom level based on actual service date span
const calculateOptimalZoomLevel = (workOrderGroups) => {
	if (!workOrderGroups || !Array.isArray(workOrderGroups) || workOrderGroups.length === 0) {
		return 2; // Default to week-by-week view if no data
	}

	// Find earliest and latest dates from all work orders and their services
	let earliestDate = null;
	let latestDate = null;

	workOrderGroups.forEach((workOrderGroup) => {
		// Check work order level dates
		const workOrderStartDate = parseDate(workOrderGroup.scheduledDate);
		const workOrderEndDate = calculateEndDate(
			workOrderGroup.scheduledDate,
			workOrderGroup.estimatedMinutes,
		);

		// Also check all services within the work order
		const services = workOrderGroup.services || [];
		services.forEach((service) => {
			const serviceStartDate = parseDate(service.scheduledDate);
			const serviceEndDate = calculateEndDate(
				service.scheduledDate,
				service.estimatedMinutes,
			);

			// Check service start date
			if (serviceStartDate) {
				if (!earliestDate || serviceStartDate < earliestDate) {
					earliestDate = serviceStartDate;
				}
			}

			// Check service end date
			if (serviceEndDate) {
				if (!latestDate || serviceEndDate > latestDate) {
					latestDate = serviceEndDate;
				}
			}
		});

		// Check work order dates too
		if (workOrderStartDate) {
			if (!earliestDate || workOrderStartDate < earliestDate) {
				earliestDate = workOrderStartDate;
			}
		}

		if (workOrderEndDate) {
			if (!latestDate || workOrderEndDate > latestDate) {
				latestDate = workOrderEndDate;
			}
		}
	});

	// If no valid dates found, use default
	if (!earliestDate || !latestDate) {
		return 2; // Default to week-by-week view
	}

	// Calculate the span in days
	const spanInDays = (latestDate - earliestDate) / (1000 * 60 * 60 * 24);

	// Determine optimal zoom level based on data span
	if (spanInDays <= 3) {
		return 4; // Day/hour view for spans of 3 days or less
	} else if (spanInDays <= 14) {
		return 3; // Week view for spans up to 2 weeks
	} else if (spanInDays <= 90) {
		return 2; // Month view for spans up to 3 months
	} else if (spanInDays <= 730) {
		// ~2 years
		return 1; // Year view for spans up to 2 years
	} else {
		return 0; // Multi-year view for very long spans
	}
};

const calculateGanttZoom = (workOrderGroups) => {
	const zoomConfig = {
		level: 1,
		levels: [
			// Level 0: Multi-year view (most zoomed out)
			{
				maxCellWidth: 120,
				minCellWidth: 40,
				scales: [
					{ unit: "year", step: 1, format: "yyyy" },
					{ unit: "quarter", step: 1, format: "Q" },
				],
			},
			// Level 1: Year view
			{
				maxCellWidth: 140,
				minCellWidth: 45,
				scales: [
					{ unit: "year", step: 1, format: "yyyy" },
					{ unit: "month", step: 1, format: localisedDate({ month: "short" }) },
				],
			},
			// Level 2: Month view
			{
				maxCellWidth: 160,
				minCellWidth: 50,
				scales: [
					{
						unit: "month",
						step: 1,
						format: localisedDate({ month: "long", year: "numeric" }),
					},
					{ unit: "week", step: 1, format: "w" },
				],
			},
			// Level 3: Week view
			{
				maxCellWidth: 180,
				minCellWidth: 40,
				scales: [
					{
						unit: "month",
						step: 1,
						format: localisedDate({ month: "long", year: "numeric" }),
					},
					{ unit: "day", step: 1, format: "d" },
				],
			},
			// Level 4: Day view (most zoomed in)
			{
				maxCellWidth: 77, // ~33% smaller (115 * 0.67)
				minCellWidth: 23, // ~33% smaller (35 * 0.67)
				scales: [
					{
						unit: "day",
						step: 1,
						format: localisedDate({ month: "short", day: "numeric" }),
					},
					{ unit: "hour", step: 1, format: "HH:mm" },
				],
			},
		],
	};

	// Get the optimal level based on actual work order group data
	const level = calculateOptimalZoomLevel(workOrderGroups);

	return {
		...zoomConfig,
		level: Math.min(level, zoomConfig.levels.length - 1),
	};
};


export const useGanttZoom = (services) => {
	const zoomConfig = useMemo(() => {
		return calculateGanttZoom(services);
	}, [services]);

	return zoomConfig;
};
